/**
 * 시스템 관리 › 결재선 관리 (v2.100.0 · admin 전용).
 *
 * ①결재자 지정 — 사내결재(부서장·경영지원·대표)·판매보고서(부서장·대표·영업지원).
 *   그전엔 운영 DB 의 app_config 를 직접 고쳐야(=개발자 호출) 했다.
 *   ⚠교체는 **진행 중 문서에도 즉시 적용**된다 — 화면에 그렇게 적어 둔다.
 * ②대리결재 — 기간(양끝 포함) 동안 대리인이 위임자의 사내결재 권한을 그대로 대신한다.
 *   문서에는 대리인 서명 + 「대결」 표기. 판매보고서 결재는 제외.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/approval-admin-api';

// ⚠toISOString 은 UTC — 한국 자정~아침에 하루 밀린다. 로컬 날짜로 만든다 (v2.100.0 실측)
const todayStr = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function UserSelect({ users, value, onChange, allowEmpty }: {
  users: api.ApprovalActorUser[];
  value: string;
  onChange: (v: string) => void;
  allowEmpty?: boolean;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="ui-input ui-input-sm min-w-[150px]">
      {allowEmpty && <option value="">— 선택 —</option>}
      {/* 지금 값이 후보에 없으면(공용 계정·비활성 — v2.231.0 · C#27 부터 후보에서 빠짐) 첫 후보로 조용히 바뀌어 보이지 않게 그대로 보여 준다 */}
      {value && !users.some((u) => u.userId === value) && (
        <option value={value} disabled>{value} (선택 불가 계정 — 바꿔 주세요)</option>
      )}
      {users.map((u) => (
        <option key={u.userId} value={u.userId}>
          {u.name}{u.title ? ` ${u.title}` : ''}{u.department ? ` · ${u.department}` : ''} ({u.userId})
        </option>
      ))}
    </select>
  );
}

export function ApprovalAdminTab() {
  const qc = useQueryClient();
  const actorsQ = useQuery({ queryKey: ['admin', 'approval', 'actors'], queryFn: api.getApprovalActors });
  const delegatesQ = useQuery({ queryKey: ['admin', 'approval', 'delegates'], queryFn: api.listDelegates });

  const [draft, setDraft] = useState<Omit<api.ApprovalActors, 'users'> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const data = actorsQ.data;
  const users = data?.users ?? [];
  const cur = draft ?? (data ? { expense: data.expense, salesReport: data.salesReport } : null);

  const [msgIsError, setMsgIsError] = useState(false);
  /* 부서 추가 입력 — 부서장이 없는 부서는 부서장 단계가 조용히 빠지므로 화면에서 채울 수 있어야 한다. */
  const [newDept, setNewDept] = useState('');
  const [newDeptHead, setNewDeptHead] = useState('');
  const saveActors = useMutation({
    mutationFn: () => api.saveApprovalActors(cur!),
    onSuccess: () => {
      setDraft(null);
      setMsgIsError(false);
      setMsg('저장했습니다 — 진행 중인 결재에도 즉시 적용됩니다.');
      qc.invalidateQueries({ queryKey: ['admin', 'approval'] });
    },
    onError: (e) => {
      // 오류를 성공과 같은 회색으로 두면 실패를 성공으로 오독한다 (검토에서 잡음)
      setMsgIsError(true);
      setMsg((e as Error).message);
    },
  });

  const [nd, setNd] = useState({ principalUserId: '', delegateUserId: '', startDate: todayStr(), endDate: todayStr(), memo: '' });
  const addDelegate = useMutation({
    mutationFn: () => api.createDelegate({ ...nd, memo: nd.memo || undefined }),
    onSuccess: () => {
      setNd({ principalUserId: '', delegateUserId: '', startDate: todayStr(), endDate: todayStr(), memo: '' });
      qc.invalidateQueries({ queryKey: ['admin', 'approval', 'delegates'] });
    },
    onError: (e) => window.alert((e as Error).message),
  });
  const delDelegate = useMutation({
    mutationFn: (id: number) => api.deleteDelegate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'approval', 'delegates'] }),
    onError: (e) => window.alert((e as Error).message),
  });

  /* 기술지원팀 월간보고서 상신 대상자 (v2.113.0) */
  const tspQ = useQuery({ queryKey: ['admin', 'approval', 'tsp-authors'], queryFn: api.listTspAuthors });
  const [newTsp, setNewTsp] = useState({ userId: '', note: '' });
  const addTsp = useMutation({
    mutationFn: () => api.addTspAuthor({ userId: newTsp.userId, note: newTsp.note || undefined }),
    onSuccess: () => {
      setNewTsp({ userId: '', note: '' });
      qc.invalidateQueries({ queryKey: ['admin', 'approval', 'tsp-authors'] });
    },
    onError: (e) => window.alert((e as Error).message),
  });
  const delTsp = useMutation({
    mutationFn: (userId: string) => api.deleteTspAuthor(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'approval', 'tsp-authors'] }),
    onError: (e) => window.alert((e as Error).message),
  });

  if (actorsQ.isLoading || !cur) {
    return <div className="text-sm text-slate-500 py-8 text-center">결재선 정보를 불러오는 중…</div>;
  }

  const setExpense = (patch: Partial<typeof cur.expense>) =>
    setDraft({ ...cur, expense: { ...cur.expense, ...patch } });

  /* 아직 부서장이 없는 부서 — **직원 명부에 실제로 있는 부서만** 고르게 해서
     오타로 만든 유령 부서가 생기지 않게 한다(그런 부서는 아무 문서도 안 와서 조용히 무의미해진다). */
  const unassignedDepts = Array.from(
    new Set(
      users
        .map((u) => (u.department ?? '').trim())
        .filter((d) => d !== ''),
    ),
  )
    .filter((d) => !(d in cur.expense.deptHeads))
    .sort((a, b) => a.localeCompare(b, 'ko'));
  const setReport = (patch: Partial<typeof cur.salesReport>) =>
    setDraft({ ...cur, salesReport: { ...cur.salesReport, ...patch } });

  return (
    <div className="space-y-5 max-w-3xl">
      {/* ── ① 결재자 지정 ─────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded p-5 space-y-4">
        <div>
          <h3 className="ui-subsection-title">결재자 지정</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            결재자를 교체하면 <b>진행 중인 문서의 결재 차례에도 즉시 적용</b>됩니다
            (이미 찍힌 도장은 그대로). 변경은 [📋 변경 이력]에 남습니다.
          </p>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-600">사내결재 (지출결재·법인카드·휴일근무일지)</div>
          {Object.entries(cur.expense.deptHeads).map(([dept, uid]) => (
            <div key={dept} className="flex items-center gap-2 text-sm">
              <span className="w-32 text-slate-500">부서장 — {dept}</span>
              <UserSelect users={users} value={uid}
                onChange={(v) => setExpense({ deptHeads: { ...cur.expense.deptHeads, [dept]: v } })} />
              <button type="button"
                onClick={() => {
                  if (Object.keys(cur.expense.deptHeads).length <= 1) {
                    window.alert('부서장은 최소 1개 부서가 필요합니다.');
                    return;
                  }
                  if (!window.confirm(
                    `「${dept}」 부서장 지정을 지웁니다.\n\n` +
                    '이 부서 사람이 올리는 문서는 부서장 승인 없이 다음 단계로 갑니다.\n계속할까요?',
                  )) return;
                  const next = { ...cur.expense.deptHeads };
                  delete next[dept];
                  setExpense({ deptHeads: next });
                }}
                className="text-xs text-rose-600 hover:text-rose-700 hover:underline px-1">
                지정 해제
              </button>
            </div>
          ))}
          {/* 부서 추가 — 이 칸이 없어서 새 부서의 부서장을 넣을 방법이 화면에 없었다 (2026-09-04) */}
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="w-32 text-slate-500">부서 추가</span>
            <select value={newDept} onChange={(e) => setNewDept(e.target.value)}
              disabled={unassignedDepts.length === 0}
              className="ui-input ui-input-sm min-w-[150px]">
              <option value="">
                {unassignedDepts.length > 0 ? '— 부서 선택 —' : '추가할 부서가 없습니다'}
              </option>
              {unassignedDepts.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <UserSelect users={users} value={newDeptHead} onChange={setNewDeptHead} allowEmpty />
            <button type="button"
              disabled={!newDept || !newDeptHead}
              onClick={() => {
                setExpense({ deptHeads: { ...cur.expense.deptHeads, [newDept]: newDeptHead } });
                setNewDept('');
                setNewDeptHead('');
              }}
              className="btn btn-dark btn-sm">
              추가
            </button>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            <b>직원 정보에 등록된 부서만</b> 고를 수 있습니다 — 오타로 만든 부서는 아무 문서도 오지 않아
            조용히 무의미해지기 때문입니다.
            <br />
            부서장이 지정되지 않은 부서는 <b>부서장 단계 없이</b> 다음 단계로 넘어갑니다(오류가 아니라 한 칸이 없는 것).
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-32 text-slate-500">경영지원 (복수)</span>
            <div className="flex flex-wrap gap-2">
              {users.map((u) => (
                <label key={u.userId} className="flex items-center gap-1 text-xs border border-slate-200 rounded px-1.5 py-0.5">
                  <input type="checkbox"
                    checked={cur.expense.supportIds.includes(u.userId)}
                    onChange={(e) => setExpense({
                      supportIds: e.target.checked
                        ? [...cur.expense.supportIds, u.userId]
                        : cur.expense.supportIds.filter((x) => x !== u.userId),
                    })} />
                  {u.name}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-32 text-slate-500">대표이사</span>
            <UserSelect users={users} value={cur.expense.ceoUserId}
              onChange={(v) => setExpense({ ceoUserId: v })} />
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-600">판매보고서</div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-32 text-slate-500">부서장</span>
            <UserSelect users={users} value={cur.salesReport.deptHeadUserId}
              onChange={(v) => setReport({ deptHeadUserId: v })} />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-32 text-slate-500">대표이사</span>
            <UserSelect users={users} value={cur.salesReport.ceoUserId}
              onChange={(v) => setReport({ ceoUserId: v })} />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-32 text-slate-500">영업지원 (결재판 표기)</span>
            <UserSelect users={users} value={cur.salesReport.salesSupportUserId}
              onChange={(v) => setReport({ salesSupportUserId: v })} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="button"
            onClick={() => {
              if (window.confirm('결재자를 교체할까요?\n진행 중인 문서의 결재 차례에도 즉시 적용됩니다.')) saveActors.mutate();
            }}
            disabled={draft === null || saveActors.isPending}
            className="btn btn-primary">
            {saveActors.isPending ? '저장 중…' : '결재자 저장'}
          </button>
          {msg && (
            <span className={`text-xs ${msgIsError ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>
              {msgIsError ? '⚠ ' : ''}{msg}
            </span>
          )}
        </div>
      </div>

      {/* ── ② 대리결재 ───────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded p-5 space-y-3">
        <div>
          <h3 className="ui-subsection-title">대리결재 (부재 시)</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            기간(양끝 포함) 동안 대리인이 위임자의 <b>사내결재 권한을 그대로</b> 대신합니다
            — 결재함·알림·승인까지. 문서에는 대리인 서명과 <b>「대결」</b>이 표기됩니다.
            판매보고서 결재는 포함되지 않습니다. 기간은 최대 90일입니다.
            <br />
            ⚠ 대리인은 기간 중 위임자가 보던 <b>결재 문서 열람 범위(금액 포함)</b>를 그대로 얻습니다
            — 대리인 지정에 유의하세요.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2 text-sm border border-slate-200 rounded p-3 bg-slate-50/50">
          <div>
            <div className="text-xs text-slate-500 mb-0.5">자리를 비우는 결재자</div>
            <UserSelect users={users} value={nd.principalUserId} allowEmpty
              onChange={(v) => setNd((s) => ({ ...s, principalUserId: v }))} />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">대신 결재할 사람</div>
            <UserSelect users={users} value={nd.delegateUserId} allowEmpty
              onChange={(v) => setNd((s) => ({ ...s, delegateUserId: v }))} />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">시작일</div>
            <input type="date" value={nd.startDate}
              onChange={(e) => setNd((s) => ({ ...s, startDate: e.target.value }))}
              className="ui-input ui-input-sm" />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">종료일</div>
            <input type="date" value={nd.endDate}
              onChange={(e) => setNd((s) => ({ ...s, endDate: e.target.value }))}
              className="ui-input ui-input-sm" />
          </div>
          <div className="flex-1 min-w-[120px]">
            <div className="text-xs text-slate-500 mb-0.5">메모 (선택)</div>
            <input type="text" value={nd.memo} placeholder="예: 해외 출장"
              onChange={(e) => setNd((s) => ({ ...s, memo: e.target.value }))}
              className="ui-input ui-input-sm w-full" />
          </div>
          <button type="button"
            onClick={() => addDelegate.mutate()}
            disabled={!nd.principalUserId || !nd.delegateUserId || addDelegate.isPending}
            className="btn btn-primary">
            등록
          </button>
        </div>

        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs">
              <th className="border border-slate-200 px-2 py-1">위임자</th>
              <th className="border border-slate-200 px-2 py-1">대리인</th>
              <th className="border border-slate-200 px-2 py-1">기간</th>
              <th className="border border-slate-200 px-2 py-1">메모</th>
              <th className="border border-slate-200 px-2 py-1 w-[64px]"></th>
            </tr>
          </thead>
          <tbody>
            {(delegatesQ.data?.rows ?? []).map((r) => (
              <tr key={r.id} className={r.active ? 'bg-emerald-50/50' : ''}>
                <td className="border border-slate-200 px-2 py-1">{r.principalName}</td>
                <td className="border border-slate-200 px-2 py-1">{r.delegateName}</td>
                <td className="border border-slate-200 px-2 py-1 text-center tabular-nums">
                  {r.startDate} ~ {r.endDate}
                  {r.active && <span className="ml-1 text-[11px] text-emerald-700 font-semibold">진행중</span>}
                </td>
                <td className="border border-slate-200 px-2 py-1 text-slate-500">{r.memo ?? ''}</td>
                <td className="border border-slate-200 px-1 py-1 text-center">
                  <button type="button"
                    onClick={() => { if (window.confirm('이 대리결재를 삭제할까요? (즉시 해제됩니다)')) delDelegate.mutate(r.id); }}
                    className="text-xs text-rose-600 hover:underline">삭제</button>
                </td>
              </tr>
            ))}
            {(delegatesQ.data?.rows ?? []).length === 0 && (
              <tr><td colSpan={5} className="border border-slate-200 px-2 py-3 text-center text-xs text-slate-500">
                등록된 대리결재가 없습니다
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── ③ 기술지원팀 월간보고서 상신 대상자 (v2.113.0) ───── */}
      <div className="bg-white border border-slate-200 rounded p-5 space-y-3">
        <div>
          <h3 className="ui-subsection-title">기술지원팀 월간보고서 — 상신 대상자</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            기술지원 포털이 월간보고서를 <b>이 명단에 있는 사람 명의로만</b> 결재에 올릴 수 있습니다.
            명단에 없는 계정 명의로 들어오면 서버가 거절합니다.
            <br />
            ⚠ 연동 열쇠(API 키)가 새어 나가도 <b>사칭 범위가 이 명단 안으로 좁혀집니다</b> — 필요한 사람만 넣어 주세요.
            <br />
            ⚠ 명단에서 빼도 <b>이미 올라간 문서는 그대로</b> 결재가 돕니다. 앞으로의 상신만 막힙니다.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2 text-sm border border-slate-200 rounded p-3 bg-slate-50/50">
          <div>
            <div className="text-xs text-slate-500 mb-0.5">대상자</div>
            <UserSelect users={users} value={newTsp.userId} allowEmpty
              onChange={(v) => setNewTsp((s) => ({ ...s, userId: v }))} />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">메모 (선택)</div>
            <input type="text" value={newTsp.note} placeholder="예: 기술지원부 월간보고 담당"
              onChange={(e) => setNewTsp((s) => ({ ...s, note: e.target.value }))}
              className="ui-input ui-input-sm w-60" />
          </div>
          <button type="button" disabled={!newTsp.userId || addTsp.isPending}
            onClick={() => addTsp.mutate()}
            className="btn btn-primary">
            {addTsp.isPending ? '등록 중…' : '+ 명단에 넣기'}
          </button>
        </div>

        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-xs">
              <th className="border border-slate-200 px-2 py-1 text-left w-[140px]">이름</th>
              <th className="border border-slate-200 px-2 py-1 text-left w-[130px]">부서</th>
              <th className="border border-slate-200 px-2 py-1 text-left w-[110px]">계정</th>
              <th className="border border-slate-200 px-2 py-1 text-left">메모</th>
              <th className="border border-slate-200 px-2 py-1 w-[54px]"></th>
            </tr>
          </thead>
          <tbody>
            {(tspQ.data?.rows ?? []).map((r) => (
              <tr key={r.userId} className={r.isActive ? '' : 'bg-slate-50 text-slate-400'}>
                <td className="border border-slate-200 px-2 py-1">
                  {r.name} <span className="text-xs text-slate-500">{r.title ?? ''}</span>
                  {!r.isActive && <span className="ml-1 text-[11px] text-rose-600">비활성 계정</span>}
                </td>
                <td className="border border-slate-200 px-2 py-1 text-slate-600">{r.department ?? ''}</td>
                <td className="border border-slate-200 px-2 py-1 font-mono text-xs">{r.userId}</td>
                <td className="border border-slate-200 px-2 py-1 text-slate-500">{r.note ?? ''}</td>
                <td className="border border-slate-200 px-1 py-1 text-center">
                  <button type="button"
                    onClick={() => {
                      if (window.confirm(`${r.name} 님을 상신 대상자에서 뺄까요?\n(이미 올라간 문서는 그대로입니다)`)) delTsp.mutate(r.userId);
                    }}
                    className="text-xs text-rose-600 hover:underline">빼기</button>
                </td>
              </tr>
            ))}
            {(tspQ.data?.rows ?? []).length === 0 && (
              <tr><td colSpan={5} className="border border-slate-200 px-2 py-3 text-center text-xs text-slate-500">
                등록된 상신 대상자가 없습니다 — 아무도 월간보고서를 올릴 수 없는 상태입니다
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
